import $ from 'jquery';
import { jwtDecode } from 'jwt-decode';
import { createContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  getAllCategoryQuestions,
  getCategoryInformation,
  getLatestRating,
  getLatestRatingByUser,
} from '../../util/DatabaseUtil';
import Button from '../Button';
import KnowQuestTab from './rating/KnowQuestTab';
import UserTab from './rating/UserTab';
import RecommendationsTab from './rating/RecommendationsTab';

export const RatingContext = createContext();

export default function RatingPageTabs({ content }) {
  const token = localStorage.getItem('loginToken');
  const userData = token ? jwtDecode(token) : '';
  const [activeTab, setActiveTab] = useState('user');
  const { cat, id, title } = useParams();

  const [rated, setRated] = useState();
  const [questions, setQuestions] = useState([]);
  const [peek] = useState(false);

  const [categoryInformation, setCategoryInformation] = useState(null);

  const [formData, setFormData] = useState({
    userId: '',
    contentName: '',
    contentId: '',
    backdrop_path: '',
    catId: '',
    timestamp: '',
    ratings: [],
    peeked: peek,
  });

  // Fetch Category Information
  // useEffect(() => {
  //   const fetchCategoryInformation = async () => {
  //     // if (cat === 'tv') {
  //     //    const info = getCategoryInformation(cat);
  //     //   setCategoryInformation(info);
  //     // } else {
  //       const info = getCategoryInformation(cat);
  //       setCategoryInformation(info);
  //     // }
  //   };

  //   fetchCategoryInformation();
  // }, [cat]);
  useEffect(() => {
    const fetchCategoryInformation = () => {
      const info = getCategoryInformation(cat);
      setCategoryInformation(info);
    };

    fetchCategoryInformation();
  }, [cat]);

  // Fetch User Ratings
  useEffect(() => {
    if (!categoryInformation) return;

    const fetchRatingByUser = async () => {
      try {
        let data;
        if (id) {
          data = await getLatestRatingByUser(
            userData.id,
            id,
            categoryInformation.ID,
          );
        } else {
          data = await getLatestRating(userData.id, categoryInformation.ID);
        }
        setRated(data);
      } catch (err) {
        console.log('Error Fetching User Rating', err);
      }
    };

    fetchRatingByUser();
  }, [activeTab, categoryInformation, id, userData.id]);

  // Fetch Category Questions
  useEffect(() => {
    if (!categoryInformation) return;
    const getQuestions = async () => {
      try {
        const response = await getAllCategoryQuestions(categoryInformation.ID);
        if (response && response.data) {
          setQuestions(response.data);
        }
      } catch (err) {
        console.log('Error Fetching Category Questions', err);
      }
    };
    getQuestions();
  }, [categoryInformation]);

  // Handle Form Data Initialization and Updates
  useEffect(() => {
    if (!categoryInformation || questions.length === 0) return;

    // Pre-fill ratings from existing 'rated' data if available, otherwise 0
    const initialRatings = questions.map((question) => {
      const existingRating = rated?.ratings?.find(
        (r) => String(r.questionId) === String(question._id),
      );
      return {
        questionId: String(question._id),
        questionTitle: question.question,
        searchKey: question.searchKey,
        rating: existingRating ? existingRating.rating : 0,
        description: question.description,
      };
    });

    const contextFields = {};
    if (rated) {
      [
        'platform',
        'subject',
        'year',
        'semester',
        'level',
        'resourceISBN',
        'resourceUrl',
        'cost',
        'company',
      ].forEach((field) => {
        if (rated[field] !== undefined) contextFields[field] = rated[field];
      });
    }

    setFormData((prev) => ({
      ...prev,
      ...contextFields,
      userId: userData.id,
      catId: categoryInformation.ID,
      contentId: id,
      contentName: content?.title || content?.name || '',
      backdrop_path: content?.backdrop_path || content?.background_image || '',
      isRecommended: rated ? rated.isRecommended : null,
      tags: rated ? rated.tags : '',
      ratings: initialRatings,
    }));
  }, [categoryInformation, questions, rated, content, id, userData.id]);

  const resetForm = () => {
    setFormData((prev) => ({
      ...prev,
      ratings: [],
    }));
    questions.forEach((question) => {
      setFormData((prev) => ({
        ...prev,
        ratings: [
          ...prev.ratings,
          {
            questionId: question._id,
            questionTitle: question.question,
            rating: 0,
            description: question.description,
          },
        ],
      }));
    });
  };

  const handleTabs = (value) => {
    setActiveTab(value);
  };

  // Show loading until categoryInformation is available
  if (!categoryInformation) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-lg font-semibold text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10 py-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full">
          <ul className="flex items-center justify-center lg:justify-start gap-4 sm:gap-8 border-b border-border">
            <li>
              <Button
                type="button"
                variant="tab"
                isActive={activeTab === 'user'}
                onClick={() => handleTabs('user')}
                className={`px-4 sm:px-6 py-2 text-base sm:text-lg font-semibold rounded-t-2xl transition-all duration-300 ${
                  activeTab === 'user'
                    ? 'text-primary font-bold bg-secondary border-b-2 mb-2 border-primary shadow-sm'
                    : 'text-secondary bg-[#111827] border-b-4 mb-2 border-transparent'
                }`}
              >
                Your Rating
              </Button>
            </li>
            <li id="knowquest">
              <Button
                type="button"
                variant="tab"
                isActive={activeTab === 'knowquest'}
                onClick={() => handleTabs('knowquest')}
                className={`px-4 sm:px-6 py-2 text-base sm:text-lg font-semibold rounded-t-2xl transition-all duration-300 ${
                  activeTab === 'knowquest'
                    ? 'text-primary font-bold bg-secondary border-b-2 mb-2 border-primary shadow-sm'
                    : 'text-secondary bg-[#111827] border-b-4 mb-2 border-transparent'
                }`}
              >
                KnowQuest Rating
              </Button>
            </li>
            <li className="" id="recommendations">
              <Button
                type="button"
                variant="tab"
                isActive={activeTab === 'recommendations'}
                onClick={() => handleTabs('recommendations')}
                className={`px-4 sm:px-6 py-2 text-base sm:text-lg font-semibold rounded-t-2xl transition-all duration-300 ${
                  activeTab === 'recommendations'
                    ? 'text-primary font-bold bg-secondary border-b-2 mb-2 border-primary shadow-sm'
                    : 'text-secondary bg-[#111827] border-b-4 mb-2 border-transparent'
                }`}
              >
                Recommendations
              </Button>
            </li>
          </ul>
        </div>
      </div>
      <div className="text-center">
        <RatingContext.Provider
          value={{
            formData,
            setFormData,
            resetForm,
            categoryInformation,
            id,
            title,
            content,
            cat,
            rated,
          }}
        >
          {activeTab === 'user' ? (
            <UserTab />
          ) : activeTab === 'knowquest' ? (
            <KnowQuestTab />
          ) : activeTab === 'recommendations' ? (
            <RecommendationsTab />
          ) : (
            <UserTab />
          )}
        </RatingContext.Provider>
      </div>
    </div>
  );
}
